import React, { useEffect, useState } from "react";
import { ColumnConfig, Waffles } from "@app/shared/waffles";
import { QESButton, QESSelect } from "@app/shared/qes-standards";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { HumanaJobStatisticsResult } from "./jobStatisticsResult";
import OperationsService from "./operationsService";

interface HumanaJobStatisticsProps {
  clients: { clientId: number; name: string }[];
  users: { userId: number; userName: string }[];
  setUsers: React.Dispatch<React.SetStateAction<{ userId: number; userName: string }[]>>;
  clientId: number | null;
  setClientId: React.Dispatch<React.SetStateAction<number | null>>;
}

const operationsService = new OperationsService();

const humanaColumns: ColumnConfig = {
  avgRunTimeSeconds: {
    displayText: "Avgerage Runtime Seconds",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__average-runtime-seconds-output-humana",
  },
  jobType: {
    displayText: "Job Type",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__job-type-output-humana",
  },
  percentComplete: {
    displayText: "Percent Complete",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__percent-complete-output-humana",
  },
  runDate: {
    displayText: "Run Date",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__run-date-output-humana",
  },
  successfulJobCount: {
    displayText: "Successful Job Count",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__successful-job-count-output-humana",
  },
  totalJobCount: {
    displayText: "Total Job Count",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__total-job-count-output-humana",
  },
  userName: {
    displayText: "User Name",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__user-name-output-humana",
  },
};

const getMostRecentSaturday = (): string => (d => (d.setDate(d.getDate() - (d.getDay() + 1) % 7), d.toISOString().split('T')[0]))(new Date());

const HumanaJobStatistics: React.FC<HumanaJobStatisticsProps> = ({ clients, users, setUsers, clientId, setClientId }) => {
  const [humanaJobStatistics, setHumanaJobStatistics] = useState<HumanaJobStatisticsResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [userId, setUserId] = useState<number | null>(null);
  const [weekStartDate, setWeekStartDate] = useState<string>(getMostRecentSaturday());

  useEffect(() => {
    if (!!clientId) {
      fetchUsers(clientId);
    }
  }, []);

  useEffect(() => {
    if (clients.find((c) => c.clientId === clientId)?.name === "Humana") {
      const integrationUser = users.find((d) => d.userName === "integration.user.humana@questanalytics.com");
      setUserId(integrationUser?.userId || null);
    }
  }, [clients, clientId, users]);

  const fetchUsers = async (clientId: number) => {
    try {
      const userData = await operationsService.getUsersByClient(clientId);
      userData.sort((a, b) => a.userName > b.userName ? 1 : -1);
      setUsers(userData);
    } catch (error) {
      console.error('Error fetching users', error);
    }
  };

  const calculateHumanaJobStatistics = async () => {
    if (!clientId) {
      setError('Please select a Client');
      return;
    }

    if (!userId) {
      setError('Please select a User');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await operationsService.getHumanaJobStatistics(weekStartDate, userId, clientId);
      setHumanaJobStatistics(response);
    } catch (error) {
      setError('Error calculating job statistics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="form-row mb-3">
        <div className="col">
          <QESSelect
            id="clientSelect"
            value={clientId !== null ? clientId.toString() : ''}
            onChange={(event) => {
              const selectedClientId = Number(event.target.value);
              setClientId(selectedClientId);
              fetchUsers(selectedClientId);
            }}
            placeholder="Select Client"
            testId="weekend-run-monitoring__client-select-humana"
            label="Client"
            required
            isValid
          >
            <option value="">Select Client</option>
            {clients.map((client) => (
              <option key={client.clientId} value={client.clientId.toString()}>
                {client.name}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <QESSelect
            id="userSelect"
            value={userId !== null ? userId.toString() : ''}
            onChange={(event) => setUserId(Number(event.target.value))}
            placeholder="Select User"
            testId="weekend-run-monitoring__user-select-humana"
            label="User"
            required
            isValid
            disabled={clientId === null}
          >
            <option value="">Select User</option>
            {users.map((user) => (
              <option key={user.userId} value={user.userId.toString()}>
                {user.userName}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <QESDateInput
            label="Week Start Date"
            value={weekStartDate}
            onChange={(event) => setWeekStartDate(event.target.value)}
            testId="weekend-run-monitoring__week-start-input-humana"
          />
        </div>
        <div className="col d-flex align-items-end">
          <QESButton onClick={calculateHumanaJobStatistics} qesType="primary" qesTestId="weekend-run-monitoring__calculate-job-statistics-humana">
            Calculate Humana Job Statistics
          </QESButton>
        </div>
      </div>
      <div className="mt-4">
        {loading && <div>Loading...</div>}
        {error && <div>{error}</div>}
        {humanaJobStatistics.length > 0 && (
          <Waffles
            columns={humanaColumns}
            data={humanaJobStatistics.map((stat, index) => ({
              /* eslint-disable sort-keys */
              key: index.toString(),
              userName: stat.userName,
              runDate: new Date(stat.runDate).toLocaleDateString(),
              jobType: stat.jobType,
              totalJobCount: stat.totalJobCount,
              successfulJobCount: stat.successfulJobCount,
              avgRunTimeSeconds: stat.avgRunTimeSeconds,
              percentComplete: stat.percentComplete,
              /* eslint-enable sort-keys */
            }))}
            enablePagination={false}
            passThroughSorting={true}
            testId="weekend-run-monitoring__job-statistics-table-humana"
          />
        )}
      </div>
    </div>
  );
};

export default HumanaJobStatistics;